'use client'

/**
 * CitationKit — the journalist / re-user block for Fridge.com citable pages
 * (owner directive 07/20 expansion #3: "JOURNALIST/CITATION KIT per data page";
 * item 197 Phase 2 extends it sitewide — recalls, press releases, local data).
 *
 * One branded card that makes Fridge.com the easiest thing in the world to cite:
 *  • pre-formatted citations — plain hyperlink HTML, APA, MLA, Chicago — each
 *    with a one-click copy button
 *  • a clear reuse-terms line (attribution + link required, per Terms §6)
 *  • on figure-bearing pages, a per-figure EMBED CODE (copy-paste HTML with the
 *    branded chart image and the attribution/link baked in) selectable across
 *    the page's figures — pages without branded figures render a clean
 *    single-column kit instead (never a fabricated embed)
 *  • a link to /contact for media inquiries
 *
 * All client-side (copy buttons + a small selector); the citation strings and
 * embed HTML derive from props, so nothing here can drift from the page.
 * Light styling, Fridge.com palette, responsive.
 */
import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Check, Quote, Code2 } from 'lucide-react'
import { CopyIcon, MailIcon } from '@/components/brand-kit/icon-sprite'
import { COMPANY_LOGO_BLUE } from '@/lib/constants'

export interface EmbeddableFigure {
  label: string
  /** Stored branded PNG (real logo baked in). */
  imageUrl: string
  /** Caption/alt for the embed. */
  caption: string
}

interface CitationKitProps {
  datasetName: string
  pageUrl: string
  /** Publication year folded into the citation strings. */
  year: string
  /** "as of" / methodology label folded into citations. */
  asOf?: string
  /** Branded embeddable figures. Omit (or pass []) on pages without any. */
  figures?: EmbeddableFigure[]
  /** Card heading. Defaults to the /data wording. */
  heading?: string
  /** Card subtitle. Defaults to the /data wording. */
  subtitle?: string
  /**
   * 'data' (default) keeps the /data reuse-terms line (methodology mention);
   * 'page' uses the general wording for non-dataset surfaces (recalls, press,
   * local reports).
   */
  docKind?: 'data' | 'page'
}

/**
 * CopyBtn — the kit's one-click copy control. Exported so the "Embed this"
 * card (components/data/embed-this-card.tsx, FRIDGE-356) reuses it rather
 * than shipping a second copy button.
 */
export function CopyBtn({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
          setDone(true)
          setTimeout(() => setDone(false), 1600)
        } catch {
          /* noop */
        }
      }}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-gray-200 bg-white text-[12px] font-bold text-gray-600 hover:border-[#ff9951] hover:text-gray-900 transition-colors shrink-0"
      aria-label={`Copy ${label}`}
    >
      {done ? <Check className="w-3.5 h-3.5 text-[#0469f0]" /> : <CopyIcon className="w-3.5 h-3.5" />}
      {done ? 'Copied' : 'Copy'}
    </button>
  )
}

export function CitationKit({
  datasetName,
  pageUrl,
  year,
  asOf,
  figures = [],
  heading = 'Cite this data',
  subtitle,
  docKind = 'data',
}: CitationKitProps) {
  const [fig, setFig] = useState(0)
  const publisher = 'Fridge.com'
  const asOfSuffix = asOf ? ` ${asOf}.` : ''
  const hasFigures = figures.length > 0
  const subtitleText =
    subtitle ??
    (hasFigures
      ? 'Press-ready citations, an embed code, and reuse terms — for journalists, researchers, and anyone referencing Fridge.com.'
      : 'Press-ready citations and reuse terms — for journalists, researchers, and anyone referencing Fridge.com.')

  const citations: Array<{ key: string; label: string; value: string; mono?: boolean }> = [
    {
      key: 'html',
      label: 'Hyperlink (HTML)',
      mono: true,
      value: `Source: <a href="${pageUrl}">${datasetName} — ${publisher}</a>`,
    },
    {
      key: 'apa',
      label: 'APA',
      value: `${publisher}. (${year}). ${datasetName}.${asOfSuffix} ${publisher}. ${pageUrl}`,
    },
    {
      key: 'mla',
      label: 'MLA',
      value: `${publisher}. "${datasetName}." ${publisher}, ${year}, ${pageUrl}.`,
    },
    {
      key: 'chicago',
      label: 'Chicago',
      value: `${publisher}. "${datasetName}." ${publisher}, ${year}. ${pageUrl}.`,
    },
    // FRIDGE-606: the two formats working journalists actually paste — AP
    // style (the newsroom default the kit oddly lacked) and a short social
    // attribution line for posts and captions.
    {
      key: 'ap',
      label: 'AP style',
      value: `According to ${datasetName}, published by ${publisher} (${pageUrl}).`,
    },
    {
      key: 'social',
      label: 'Social attribution',
      value: `Data: ${datasetName} — ${publisher} ${pageUrl}`,
    },
  ]

  const current = figures[fig]
  // The embed code below keeps the ABSOLUTE fridge.com URL (journalists paste it
  // into their own pages). For the on-page preview <img>, use a same-origin path
  // so it loads against whatever host serves this page — fridge.com in prod,
  // localhost in dev — instead of hard-pinning to production.
  const previewSrc = current
    ? current.imageUrl.replace(/^https?:\/\/(www\.)?fridge\.com(?=\/)/i, '')
    : ''
  const embedCode = current
    ? `<figure style="margin:0;max-width:720px;font-family:system-ui,sans-serif">
  <img src="${current.imageUrl}" alt="${current.caption}" style="width:100%;height:auto;border:1px solid #e5e7eb;border-radius:12px" />
  <figcaption style="font-size:13px;color:#4b5563;margin-top:8px">
    ${current.caption} Source: <a href="${pageUrl}" style="color:#0469f0;font-weight:600">${datasetName} — ${publisher}</a>.
  </figcaption>
</figure>`
    : ''

  return (
    <section aria-labelledby="citation-kit" className="mt-14 md:mt-16">
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-6 md:px-8 py-5 border-b border-gray-200 bg-[#0469f0]/[0.03] flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#0469f0]/10 flex items-center justify-center shrink-0">
            <Quote className="w-5 h-5 text-[#0469f0]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="citation-kit" className="text-lg font-bold text-gray-900">
              {heading}
            </h2>
            <p className="text-[13px] text-gray-600">{subtitleText}</p>
          </div>
          {/* FRIDGE-606: the kit carries the brand mark — a citation block IS
              a share surface, and every share carries Fridge.com. */}
          <Image
            src={COMPANY_LOGO_BLUE}
            alt="Fridge.com"
            width={182}
            height={32}
            className="hidden sm:block h-7 w-auto object-contain shrink-0"
            // FRIDGE-1540: the class's 28 px and the file's aspect ratio (2215 x 391) inline,
            // so Chromium counts this lazy image as sized; the drawing is unchanged.
            style={{ height: 28, aspectRatio: '2215 / 391' }}
          />
        </div>

        <div
          className={`p-6 md:p-8 grid grid-cols-1 gap-8 ${hasFigures ? 'lg:grid-cols-2' : ''}`}
        >
          {/* Citations */}
          <div className="min-w-0">
            <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-3">
              Pre-formatted citations
            </h3>
            <div className="space-y-3">
              {citations.map((c) => (
                <div
                  key={c.key}
                  // FRIDGE-606: the whole card reads as a control — hover lifts
                  // it to the brand orange, cursor invites the copy.
                  className="group/cite rounded-lg border border-gray-200 p-3.5 transition-all hover:border-[#ff9951] hover:shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="text-[12px] font-bold text-gray-900">{c.label}</span>
                    <CopyBtn text={c.value} label={`${c.label} citation`} />
                  </div>
                  <p
                    className={`text-[13px] text-gray-700 leading-relaxed break-words ${
                      c.mono ? 'font-mono text-[12px] bg-gray-50 rounded p-2' : ''
                    }`}
                  >
                    {c.value}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-lg bg-[#ff9951]/[0.08] border border-[#ff9951]/30 p-3.5">
              <p className="text-[13px] text-gray-700 leading-relaxed">
                <strong className="text-gray-900">Reuse terms:</strong>{' '}
                {docKind === 'data' ? (
                  <>
                    Fridge.com data is free to cite, quote, and share <em>with attribution</em> —
                    credit Fridge.com and link back to this page. Please keep the methodology
                    version alongside any figure.
                  </>
                ) : (
                  <>
                    This page is free to cite, quote, and share <em>with attribution</em> — credit
                    Fridge.com and link back to this page.
                  </>
                )}
              </p>
            </div>
            {!hasFigures && (
              <Link
                href="/contact"
                className="mt-4 inline-flex items-center gap-2 text-[13px] font-bold text-[#0469f0] hover:text-[#004aad]"
              >
                <MailIcon className="w-4 h-4" />
                Media inquiries — contact Fridge.com
              </Link>
            )}
          </div>

          {/* Embed + contact — only on pages with real branded figures */}
          {hasFigures && (
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <Code2 className="w-4 h-4 text-[#0469f0]" />
              <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
                Embed a chart (attribution baked in)
              </h3>
            </div>
            {figures.length > 1 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {figures.map((f, i) => (
                  <button
                    key={f.label}
                    type="button"
                    onClick={() => setFig(i)}
                    className={`text-[12px] font-semibold px-3 h-8 rounded-full border transition-colors ${
                      i === fig
                        ? 'border-[#0469f0] bg-[#0469f0]/[0.06] text-[#0469f0]'
                        : 'border-gray-200 text-gray-600 hover:border-[#ff9951]'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            {current && (
              <>
                <div className="rounded-lg border border-gray-200 overflow-hidden mb-3">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={previewSrc} alt={current.caption} className="w-full h-auto block" />
                </div>
                <div className="rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between gap-3 px-3.5 py-2 border-b border-gray-100">
                    <span className="text-[12px] font-bold text-gray-900">Copy-paste HTML</span>
                    <CopyBtn text={embedCode} label="embed code" />
                  </div>
                  <pre className="text-[11px] leading-relaxed text-gray-700 bg-gray-50 p-3 overflow-x-auto m-0 whitespace-pre-wrap break-words">
                    {embedCode}
                  </pre>
                </div>
              </>
            )}
            <Link
              href="/contact"
              className="mt-4 inline-flex items-center gap-2 text-[13px] font-bold text-[#0469f0] hover:text-[#004aad]"
            >
              <MailIcon className="w-4 h-4" />
              Media inquiries — contact Fridge.com
            </Link>
          </div>
          )}
        </div>
      </div>
    </section>
  )
}
